import { createTheme } from "@mui/material/styles";
import { SLATE, INDIGO, BLUE, EMERALD, RED, AMBER } from "./constants/Colors";
import {
  GENERIC_BUTTON_FONT_SIZE,
  GENERIC_BUTTON_HEIGHT,
  GENERIC_EDIT_PLACEHORDER_FONT_SIZE,
  GENERIC_LABEL_L_FONT_SIZE,
} from "./constants/Constants";

// ─── Base theme ───────────────────────────────────────────────────────────────

const theme = createTheme({
  // ── Palette ────────────────────────────────────────────────────────────────
  palette: {
    mode: "light",
    primary: {
      main: INDIGO[600],
      light: INDIGO[400],
      dark: INDIGO[700],
      contrastText: "#ffffff",
    },
    secondary: {
      main: BLUE[600],
      light: BLUE[500],
      dark: BLUE[700],
      contrastText: "#ffffff",
    },
    background: {
      default: "#f5f5f7",
      paper: "#ffffff",
    },
    text: {
      primary: "#1d1d1f",
      secondary: "#6e6e73",
      disabled: SLATE[400],
    },
    divider: SLATE[200],
    action: {
      disabledBackground: SLATE[100],
    },
    success: {
      main: EMERALD[500],
      light: EMERALD[50],
      dark: EMERALD[600],
      contrastText: "#ffffff",
    },
    error: {
      main: RED[600],
      light: RED[50],
      contrastText: "#ffffff",
    },
    warning: {
      main: AMBER[500],
      light: AMBER[50],
      dark: AMBER[700],
    },
    info: {
      main: BLUE[600],
      light: BLUE[50],
    },
  },

  // ── Typography ─────────────────────────────────────────────────────────────
  typography: {
    fontFamily: "'Sora', sans-serif",
    fontWeightLight: 300,
    fontWeightRegular: 400,
    fontWeightMedium: 500,
    fontWeightBold: 600,

    h1: {
      fontSize: "2.25rem",
      fontWeight: 600,
      lineHeight: 1.2,
      letterSpacing: "-0.02em",
    },
    h2: { fontSize: "1.875rem", fontWeight: 600, lineHeight: 1.25 },
    h3: { fontSize: "1.5rem", fontWeight: 600, lineHeight: 1.3 },
    h4: { fontSize: "1.25rem", fontWeight: 600, lineHeight: 1.35 },
    h5: { fontSize: "1.125rem", fontWeight: 600, lineHeight: 1.4 },
    h6: { fontSize: "1rem", fontWeight: 600, lineHeight: 1.4 },

    subtitle1: { fontSize: "0.875rem", fontWeight: 500, color: SLATE[700] },
    subtitle2: { fontSize: "0.75rem", fontWeight: 500, color: SLATE[600] },

    body1: { fontSize: "0.875rem", fontWeight: 400, lineHeight: 1.6 },
    body2: { fontSize: "0.75rem", fontWeight: 400, lineHeight: 1.5 },

    caption: {
      fontSize: "0.75rem",
      fontWeight: 400,
      color: SLATE[500],
    },
    overline: {
      fontSize: "0.6875rem",
      fontWeight: 600,
      letterSpacing: "0.08em",
      textTransform: "uppercase",
    },
    button: {
      fontSize: "0.875rem",
      fontWeight: 500,
      textTransform: "none",
    },
  },

  // ── Shape ──────────────────────────────────────────────────────────────────
  shape: {
    borderRadius: 8,
  },

  // ── Shadows ────────────────────────────────────────────────────────────────
  shadows: [
    "none",
    "0 1px 2px 0 rgba(0,0,0,0.05)", // 1 — sm
    "0 1px 3px 0 rgba(0,0,0,0.10), 0 1px 2px -1px rgba(0,0,0,0.10)", // 2
    "0 4px 6px -1px rgba(0,0,0,0.10), 0 2px 4px -2px rgba(0,0,0,0.10)", // 3 — md
    "0 10px 15px -3px rgba(0,0,0,0.10), 0 4px 6px -4px rgba(0,0,0,0.10)", // 4 — lg
    "0 20px 25px -5px rgba(0,0,0,0.10), 0 8px 10px -6px rgba(0,0,0,0.10)", // 5 — xl
    "0 25px 50px -12px rgba(0,0,0,0.25)", // 6 — 2xl
    "0 1px 2px 0 rgba(0,0,0,0.05)",
    "0 1px 3px 0 rgba(0,0,0,0.10), 0 1px 2px -1px rgba(0,0,0,0.10)",
    "0 4px 6px -1px rgba(0,0,0,0.10), 0 2px 4px -2px rgba(0,0,0,0.10)",
    "0 10px 15px -3px rgba(0,0,0,0.10), 0 4px 6px -4px rgba(0,0,0,0.10)",
    "0 20px 25px -5px rgba(0,0,0,0.10), 0 8px 10px -6px rgba(0,0,0,0.10)",
    "0 25px 50px -12px rgba(0,0,0,0.25)",
    "0 1px 3px 0 rgba(0,0,0,0.10), 0 1px 2px -1px rgba(0,0,0,0.10)",
    "0 4px 6px -1px rgba(0,0,0,0.10), 0 2px 4px -2px rgba(0,0,0,0.10)",
    "0 10px 15px -3px rgba(0,0,0,0.10), 0 4px 6px -4px rgba(0,0,0,0.10)",
    "0 20px 25px -5px rgba(0,0,0,0.10), 0 8px 10px -6px rgba(0,0,0,0.10)",
    "0 25px 50px -12px rgba(0,0,0,0.25)",
    "0 1px 3px 0 rgba(0,0,0,0.10), 0 1px 2px -1px rgba(0,0,0,0.10)",
    "0 4px 6px -1px rgba(0,0,0,0.10), 0 2px 4px -2px rgba(0,0,0,0.10)",
    "0 10px 15px -3px rgba(0,0,0,0.10), 0 4px 6px -4px rgba(0,0,0,0.10)",
    "0 20px 25px -5px rgba(0,0,0,0.10), 0 8px 10px -6px rgba(0,0,0,0.10)",
    "0 25px 50px -12px rgba(0,0,0,0.25)",
    "0 1px 3px 0 rgba(0,0,0,0.10), 0 1px 2px -1px rgba(0,0,0,0.10)",
    "0 4px 6px -1px rgba(0,0,0,0.10), 0 2px 4px -2px rgba(0,0,0,0.10)",
  ],

  // ── Transitions ────────────────────────────────────────────────────────────
  transitions: {
    duration: {
      shortest: 150,
      shorter: 200,
      short: 250,
      standard: 300,
      complex: 375,
      enteringScreen: 225,
      leavingScreen: 195,
    },
  },

  // ── Component Overrides ────────────────────────────────────────────────────
  components: {
    // ── Button ──────────────────────────────────────────────────────────────
    MuiButton: {
      defaultProps: {
        disableElevation: true,
      },
      variants: [
        // Legacy variant kept for existing pages
        {
          props: { variant: "PrimaryButton" },
          style: {
            maxHeight: GENERIC_BUTTON_HEIGHT,
            padding: "8px 20px",
            gap: "6px",
            borderRadius: "980px",
            background: INDIGO[600],
            border: "none",
            color: "#fff",
            fontSize: GENERIC_BUTTON_FONT_SIZE,
            fontWeight: 500,
            textTransform: "none",
            "&:hover": {
              background: INDIGO[700],
            },
            "&:disabled": {
              background: SLATE[200],
              color: SLATE[400],
              border: "none",
            },
          },
        },
        {
          props: { variant: "PrimaryButtonBlue" },
          style: {
            maxHeight: GENERIC_BUTTON_HEIGHT,
            padding: "8px 20px",
            gap: "6px",
            borderRadius: "980px",
            background: INDIGO[600],
            border: "none",
            color: "#fff",
            fontSize: GENERIC_BUTTON_FONT_SIZE,
            fontWeight: 500,
            textTransform: "none",
            "&:hover": {
              background: INDIGO[700],
            },
            "&:disabled": {
              background: SLATE[200],
              color: SLATE[400],
            },
          },
        },
        {
          props: { variant: "SecondaryButton" },
          style: {
            maxHeight: GENERIC_BUTTON_HEIGHT,
            padding: "8px 16px",
            gap: "6px",
            borderRadius: "8px",
            backgroundColor: "#ffffff",
            border: `1px solid ${SLATE[200]}`,
            color: SLATE[700],
            fontSize: GENERIC_BUTTON_FONT_SIZE,
            fontWeight: 500,
            textTransform: "none",
            "&:hover": {
              backgroundColor: SLATE[50],
              borderColor: SLATE[300],
            },
          },
        },
        {
          props: { variant: "LightBlueBGButton" },
          style: {
            maxHeight: GENERIC_BUTTON_HEIGHT,
            padding: "8px 16px",
            gap: "6px",
            borderRadius: "8px",
            background: INDIGO[50],
            color: INDIGO[600],
            border: `1px solid ${INDIGO[100]}`,
            fontSize: GENERIC_BUTTON_FONT_SIZE,
            fontWeight: 500,
            textTransform: "none",
            "&:hover": {
              background: INDIGO[100],
            },
          },
        },
      ],
      styleOverrides: {
        root: {
          borderRadius: "980px",
          fontWeight: 500,
          fontSize: "0.875rem",
          textTransform: "none",
          transition: "all 0.2s ease",
        },
        contained: {
          background: INDIGO[600],
          color: "#ffffff",
          border: "none",
          borderRadius: "980px",
          "&:hover": {
            background: INDIGO[700],
          },
          "&.Mui-disabled": {
            background: SLATE[200],
            color: SLATE[400],
          },
        },
        outlined: {
          borderColor: SLATE[200],
          color: SLATE[700],
          backgroundColor: "#ffffff",
          "&:hover": {
            backgroundColor: SLATE[50],
            borderColor: SLATE[300],
          },
        },
        text: {
          color: SLATE[600],
          "&:hover": {
            backgroundColor: SLATE[100],
            color: SLATE[900],
          },
        },
        sizeSmall: {
          padding: "4px 12px",
          fontSize: "0.75rem",
        },
        sizeMedium: {
          padding: "8px 16px",
        },
        sizeLarge: {
          padding: "10px 24px",
          fontSize: "0.9375rem",
        },
      },
    },

    // ── IconButton ──────────────────────────────────────────────────────────
    MuiIconButton: {
      styleOverrides: {
        root: {
          color: SLATE[500],
          borderRadius: "8px",
          transition: "all 0.2s ease",
          "&:hover": {
            backgroundColor: SLATE[100],
            color: SLATE[700],
          },
        },
        sizeSmall: { padding: "4px" },
      },
    },

    // ── Card ────────────────────────────────────────────────────────────────
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: "16px",
          border: `1px solid ${SLATE[200]}`,
          boxShadow: "0 1px 2px 0 rgba(0,0,0,0.05)",
          backgroundColor: "#ffffff",
          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          "&:hover": {
            boxShadow:
              "0 10px 15px -3px rgba(0,0,0,0.10), 0 4px 6px -4px rgba(0,0,0,0.10)",
          },
        },
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: {
          padding: "20px",
          "&:last-child": { paddingBottom: "20px" },
        },
      },
    },

    // ── Paper ───────────────────────────────────────────────────────────────
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: "none",
        },
        elevation0: { boxShadow: "none", border: `1px solid ${SLATE[200]}` },
        elevation1: {
          boxShadow: "0 1px 2px 0 rgba(0,0,0,0.05)",
          border: `1px solid ${SLATE[200]}`,
        },
        elevation2: {
          boxShadow:
            "0 4px 6px -1px rgba(0,0,0,0.10), 0 2px 4px -2px rgba(0,0,0,0.10)",
        },
        rounded: { borderRadius: "16px" },
      },
    },

    // ── TextField ───────────────────────────────────────────────────────────
    MuiTextField: {
      defaultProps: {
        inputProps: { autoComplete: "off" },
      },
      styleOverrides: {
        root: {
          "& .MuiOutlinedInput-root": {
            borderRadius: "8px",
            backgroundColor: "#ffffff",
            fontSize: GENERIC_EDIT_PLACEHORDER_FONT_SIZE,
            transition: "box-shadow 0.2s ease",
            "& fieldset": {
              borderColor: SLATE[300],
              transition: "border-color 0.2s ease",
            },
            "&:hover fieldset": {
              borderColor: SLATE[400],
            },
            "&.Mui-focused fieldset": {
              borderColor: INDIGO[500],
              borderWidth: "1.5px",
            },
            "&.Mui-focused": {
              boxShadow: "0 0 0 3px rgba(99, 102, 241, 0.10)",
            },
            "&.Mui-error fieldset": {
              borderColor: RED[300],
            },
          },
          "& .MuiInputBase-input": {
            color: SLATE[900],
            padding: "8px 12px",
            "&::placeholder": { color: SLATE[400], opacity: 1 },
          },
          "& .MuiInputLabel-root": {
            color: SLATE[500],
            fontSize: GENERIC_EDIT_PLACEHORDER_FONT_SIZE,
            "&.Mui-focused": { color: INDIGO[600] },
          },
          "& .MuiFormHelperText-root.Mui-error": {
            color: RED[600],
          },
          // Remove legacy fieldset hide / inline border hacks
          "& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline": {
            display: "block",
          },
        },
      },
    },

    // ── Autocomplete ────────────────────────────────────────────────────────
    MuiAutocomplete: {
      styleOverrides: {
        paper: {
          borderRadius: "10px",
          border: `1px solid ${SLATE[200]}`,
          boxShadow:
            "0 10px 15px -3px rgba(0,0,0,0.10), 0 4px 6px -4px rgba(0,0,0,0.10)",
          marginTop: "4px",
        },
        option: {
          fontSize: GENERIC_EDIT_PLACEHORDER_FONT_SIZE,
          color: SLATE[700],
          borderRadius: "6px",
          margin: "2px 4px",
          padding: "8px 12px",
          "&[aria-selected='true']": {
            backgroundColor: `${INDIGO[50]} !important`,
            color: INDIGO[700],
          },
          "&.Mui-focused": {
            backgroundColor: SLATE[50],
          },
        },
        root: {
          "& .MuiOutlinedInput-root": { padding: "0px" },
          "& .Mui-error": { color: `${RED[600]} !important` },
          "& input": {
            padding: "8px 12px !important",
            fontSize: GENERIC_EDIT_PLACEHORDER_FONT_SIZE,
            "&::placeholder": { color: SLATE[400] },
          },
        },
      },
    },

    // ── Select ──────────────────────────────────────────────────────────────
    MuiSelect: {
      styleOverrides: {
        root: {
          borderRadius: "8px",
          fontSize: GENERIC_EDIT_PLACEHORDER_FONT_SIZE,
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: "8px",
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: INDIGO[500],
            borderWidth: "1.5px",
          },
          "&.Mui-focused": {
            boxShadow: "0 0 0 3px rgba(99, 102, 241, 0.10)",
          },
        },
      },
    },

    // ── Chip ────────────────────────────────────────────────────────────────
    MuiChip: {
      variants: [
        {
          props: { variant: "TableAddTag" },
          style: {
            height: "26px",
            fontFamily: "'Sora', sans-serif",
            fontSize: "14px",
            fontWeight: 400,
            background: "transparent",
            borderRadius: "18px",
            padding: "5px 15px",
            border: `1px solid ${SLATE[300]}`,
            textTransform: "none",
            color: SLATE[600],
          },
        },
      ],
      styleOverrides: {
        root: {
          borderRadius: "6px",
          fontSize: "0.75rem",
          fontWeight: 500,
          height: "24px",
        },
        filled: {
          backgroundColor: SLATE[100],
          color: SLATE[700],
          "&:hover": { backgroundColor: SLATE[200] },
        },
        outlined: {
          borderColor: SLATE[200],
          color: SLATE[600],
        },
        colorPrimary: {
          backgroundColor: INDIGO[50],
          color: INDIGO[700],
          border: `1px solid ${INDIGO[100]}`,
        },
      },
    },

    // ── AppBar / TopBar ─────────────────────────────────────────────────────
    MuiAppBar: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: {
          backgroundColor: "rgba(255,255,255,0.85)",
          backdropFilter: "blur(20px)",
          WebkitBackdropFilter: "blur(20px)",
          borderBottom: "1px solid rgba(0,0,0,0.07)",
          color: "#1d1d1f",
          "& .MuiToolbar-root": {
            justifyContent: "space-between",
          },
        },
      },
    },
    MuiToolbar: {
      styleOverrides: {
        root: {
          minHeight: "56px !important",
          padding: "0 24px",
        },
      },
    },

    // ── Drawer / Sidebar ────────────────────────────────────────────────────
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: "#ffffff",
          borderRight: `1px solid ${SLATE[200]}`,
          boxShadow: "0 10px 15px -3px rgba(0,0,0,0.10)",
        },
      },
    },

    // ── List / Nav Items ────────────────────────────────────────────────────
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: "8px",
          padding: "8px 12px",
          fontSize: "0.875rem",
          color: SLATE[600],
          transition: "all 0.15s ease",
          marginBottom: "0 !important",
          fontWeight: 400,
          lineHeight: "18px",
          "&:hover": {
            backgroundColor: SLATE[50],
            color: SLATE[900],
          },
          "&.Mui-selected": {
            backgroundColor: INDIGO[50],
            color: INDIGO[700],
            fontWeight: 500,
            "& .MuiListItemIcon-root": { color: INDIGO[600] },
            "&:hover": { backgroundColor: INDIGO[50] },
          },
        },
      },
    },
    MuiListItemIcon: {
      styleOverrides: {
        root: {
          color: SLATE[500],
          minWidth: "36px",
        },
      },
    },
    MuiListItemText: {
      styleOverrides: {
        root: {
          "& .MuiTypography-root": {
            fontSize: GENERIC_LABEL_L_FONT_SIZE,
          },
        },
        primary: {
          fontSize: "0.875rem",
          fontWeight: 400,
        },
      },
    },

    // ── Table ───────────────────────────────────────────────────────────────
    MuiTableContainer: {
      styleOverrides: {
        root: {
          borderRadius: "12px",
          border: `1px solid ${SLATE[200]}`,
          boxShadow: "none",
          fontSize: 13,
          "& .MuiTableCell-root": {
            borderBottom: `1px solid ${SLATE[200]}`,
            verticalAlign: "middle",
            paddingTop: "0px",
            paddingBottom: "0px",
            height: "45px",
          },
          "& .MuiTableCell-head": {
            backgroundColor: SLATE[50],
            fontFamily: "'Sora', sans-serif",
            fontSize: "12px",
            fontWeight: 600,
            lineHeight: "16px",
            textAlign: "left",
            height: "48px",
            textTransform: "uppercase",
            letterSpacing: "0.05em",
            color: SLATE[600],
            verticalAlign: "middle !important",
          },
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          "&:hover": { backgroundColor: SLATE[50] },
          "&:last-child td": { borderBottom: "none" },
        },
      },
    },

    // ── Dialog / Modal ──────────────────────────────────────────────────────
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: "16px",
          border: `1px solid ${SLATE[200]}`,
          boxShadow: "0 25px 50px -12px rgba(0,0,0,0.25)",
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          fontSize: "1rem",
          fontWeight: 600,
          color: SLATE[900],
          padding: "20px 24px 16px",
          borderBottom: `1px solid ${SLATE[200]}`,
        },
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: { padding: "20px 24px" },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          padding: "16px 24px",
          borderTop: `1px solid ${SLATE[200]}`,
          gap: "8px",
        },
      },
    },

    // ── Menu / Dropdown ─────────────────────────────────────────────────────
    MuiMenu: {
      styleOverrides: {
        paper: {
          borderRadius: "10px",
          border: `1px solid ${SLATE[200]}`,
          boxShadow:
            "0 10px 15px -3px rgba(0,0,0,0.10), 0 4px 6px -4px rgba(0,0,0,0.10)",
          marginTop: "4px",
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          fontSize: "0.875rem",
          color: SLATE[700],
          borderRadius: "6px",
          margin: "2px 4px",
          padding: "8px 12px",
          minHeight: "36px",
          "&:hover": { backgroundColor: SLATE[50] },
          "&.Mui-selected": {
            backgroundColor: INDIGO[50],
            color: INDIGO[700],
            "&:hover": { backgroundColor: INDIGO[50] },
          },
        },
      },
    },

    // ── Tooltip ─────────────────────────────────────────────────────────────
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: SLATE[900],
          color: "#ffffff",
          fontSize: "0.75rem",
          fontWeight: 400,
          borderRadius: "8px",
          padding: "6px 10px",
          boxShadow: "0 4px 6px -1px rgba(0,0,0,0.10)",
        },
        arrow: { color: SLATE[900] },
      },
    },

    // ── Tabs ────────────────────────────────────────────────────────────────
    MuiTabs: {
      styleOverrides: {
        root: {
          borderBottom: `1px solid ${SLATE[200]}`,
          minHeight: "44px",
        },
        indicator: {
          backgroundColor: INDIGO[600],
          height: "2px",
          borderRadius: "2px 2px 0 0",
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          fontSize: "0.875rem",
          fontWeight: 400,
          color: SLATE[500],
          textTransform: "none",
          minHeight: "44px",
          padding: "0 16px",
          "&.Mui-selected": {
            color: INDIGO[600],
            fontWeight: 500,
          },
          "&:hover": { color: SLATE[700] },
        },
      },
    },

    // ── Alert ───────────────────────────────────────────────────────────────
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: "10px",
          fontSize: "0.875rem",
          border: "1px solid",
        },
        standardSuccess: {
          backgroundColor: EMERALD[50],
          borderColor: "#bbf7d0",
          color: "#065f46",
        },
        standardError: {
          backgroundColor: RED[50],
          borderColor: RED[200],
          color: "#991b1b",
        },
        standardWarning: {
          backgroundColor: AMBER[50],
          borderColor: "#fde68a",
          color: "#92400e",
        },
        standardInfo: {
          backgroundColor: BLUE[50],
          borderColor: "#bfdbfe",
          color: "#1e40af",
        },
      },
    },

    // ── Switch ──────────────────────────────────────────────────────────────
    MuiSwitch: {
      styleOverrides: {
        switchBase: {
          "&.Mui-checked": {
            color: INDIGO[600],
            "& + .MuiSwitch-track": {
              backgroundColor: INDIGO[500],
              opacity: 1,
            },
          },
        },
        track: {
          backgroundColor: SLATE[300],
          opacity: 1,
          borderRadius: "12px",
        },
      },
    },

    // ── Checkbox / Radio ────────────────────────────────────────────────────
    MuiCheckbox: {
      styleOverrides: {
        root: {
          color: SLATE[300],
          padding: "6px",
          "&.Mui-checked": { color: INDIGO[600] },
          "&:hover": { backgroundColor: INDIGO[50] },
        },
      },
    },
    MuiRadio: {
      styleOverrides: {
        root: {
          color: SLATE[300],
          padding: "6px",
          "&.Mui-checked": { color: INDIGO[600] },
        },
      },
    },

    // ── Avatar ──────────────────────────────────────────────────────────────
    MuiAvatar: {
      styleOverrides: {
        root: {
          backgroundColor: INDIGO[50],
          color: INDIGO[600],
          fontSize: "0.875rem",
          fontWeight: 600,
        },
      },
    },

    // ── Divider ─────────────────────────────────────────────────────────────
    MuiDivider: {
      styleOverrides: {
        root: { borderColor: SLATE[200] },
      },
    },

    // ── Badge ───────────────────────────────────────────────────────────────
    MuiBadge: {
      styleOverrides: {
        badge: {
          fontSize: "0.6875rem",
          fontWeight: 600,
          minWidth: "18px",
          height: "18px",
          borderRadius: "9px",
        },
      },
    },

    // ── CircularProgress / LinearProgress ───────────────────────────────────
    MuiCircularProgress: {
      defaultProps: { size: 24 },
      styleOverrides: {
        root: { color: INDIGO[600] },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          borderRadius: "4px",
          backgroundColor: SLATE[200],
          height: "4px",
        },
        bar: {
          borderRadius: "4px",
          background: `linear-gradient(to right, ${BLUE[600]}, ${INDIGO[600]})`,
        },
      },
    },

    // ── Skeleton ────────────────────────────────────────────────────────────
    MuiSkeleton: {
      styleOverrides: {
        root: {
          backgroundColor: SLATE[100],
          "&::after": {
            background: `linear-gradient(90deg, transparent, ${SLATE[200]}, transparent)`,
          },
        },
      },
    },
  },
});

export default createTheme(theme);
